import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getRequiredCurrentUserClientId } from "../../../core/auth/tenant";
import { getReferralDashboardData } from "../services/referralsService";

const DEFAULT_REFERRAL_LINK = "https://pbxscribe.com/ref/user123";
const DEFAULT_STATS = [
  { label: "Total Clicks", value: "0" },
  { label: "Total Signups", value: "0" },
  { label: "Active Referrals", value: "0" },
  { label: "Total Earnings", value: "$0.00" },
];

const DEFAULT_REFERRALS = [];

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatAmount = (amount) => {
  const amountNumber = Number(amount);
  if (Number.isNaN(amountNumber)) return "$0.00";
  return `$${amountNumber.toFixed(2)}`;
};

const toTitleCase = (value) => {
  if (!value) return "Active";
  const text = String(value);
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

const resolveCompanyName = (item) => {
  const directReferralClient = item?.referral_client;
  if (typeof directReferralClient === "string" && directReferralClient.trim()) {
    return directReferralClient;
  }
  if (directReferralClient && typeof directReferralClient === "object") {
    return (
      directReferralClient.client_name ||
      directReferralClient.name ||
      directReferralClient.company ||
      "N/A"
    );
  }

  const directClient = item?.client;
  if (typeof directClient === "string" && directClient.trim()) return directClient;
  if (directClient && typeof directClient === "object") {
    return directClient.client_name || directClient.name || directClient.company || "N/A";
  }

  return "N/A";
};

const resolveReferralEmail = (item) => {
  if (typeof item?.referral_client_email === "string" && item.referral_client_email.trim()) {
    return item.referral_client_email;
  }

  const referralClient = item?.referral_client;
  if (referralClient && typeof referralClient === "object") {
    if (typeof referralClient.client_email === "string" && referralClient.client_email.trim()) {
      return referralClient.client_email;
    }
    if (typeof referralClient.email === "string" && referralClient.email.trim()) {
      return referralClient.email;
    }
  }

  const client = item?.client;
  if (client && typeof client === "object") {
    if (typeof client.client_email === "string" && client.client_email.trim()) {
      return client.client_email;
    }
    if (typeof client.email === "string" && client.email.trim()) {
      return client.email;
    }
  }

  return "N/A";
};

export const fetchReferralDashboardThunk = createAsyncThunk(
  "referral/fetchReferralDashboard",
  async (clientId, { getState, rejectWithValue }) => {
    const resolvedClientId = Number(clientId) || getRequiredCurrentUserClientId(getState());
    const response = await getReferralDashboardData(resolvedClientId, {
      limit: 20,
      offset: 0,
    });

    if (response?.error) {
      return rejectWithValue(response.error.message || "Unable to load referral data.");
    }

    const apiReferrals = Array.isArray(response?.referrals) ? response.referrals : [];

    const mappedReferrals = apiReferrals.map((item) => {
      const statusFromApi = String(item?.referral_status || "").trim();
      const fallbackStatus = item?.invoice_id ? "Active" : "Inactive";

      return {
        company: resolveCompanyName(item),
        email: resolveReferralEmail(item),
        status: toTitleCase(statusFromApi || fallbackStatus),
        earnings: formatAmount(item?.referral_bonus),
        date: formatDate(item?.created_at),
      };
    });

    const statsPayload = response?.stats || {};

    return {
      referralLink: response?.referralLink || DEFAULT_REFERRAL_LINK,
      referrals: mappedReferrals,
      totalReferrals: Number(response?.total || mappedReferrals.length),
      statsData: [
        { label: "Total Clicks", value: statsPayload.totalClicks || "0" },
        { label: "Total Signups", value: statsPayload.totalSignups || "0" },
        { label: "Active Referrals", value: statsPayload.activeReferrals || "0" },
        { label: "Total Earnings", value: statsPayload.totalEarnings || "$0.00" },
      ],
    };
  },
);

const initialState = {
  currentPage: 0,
  isLoadingReferrals: false,
  referrals: DEFAULT_REFERRALS,
  totalReferrals: 0,
  referralLink: DEFAULT_REFERRAL_LINK,
  statsData: DEFAULT_STATS,
  error: "",
  hasLoaded: false,
};

export const referralInitialState = initialState;

const referralSlice = createSlice({
  name: "referral",
  initialState,
  reducers: {
    setReferralCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReferralDashboardThunk.pending, (state) => {
        state.isLoadingReferrals = true;
        state.error = "";
      })
      .addCase(fetchReferralDashboardThunk.fulfilled, (state, action) => {
        state.isLoadingReferrals = false;
        state.referralLink = action.payload.referralLink;
        state.referrals = action.payload.referrals;
        state.totalReferrals = action.payload.totalReferrals;
        state.statsData = action.payload.statsData;
        state.hasLoaded = true;
      })
      .addCase(fetchReferralDashboardThunk.rejected, (state, action) => {
        state.isLoadingReferrals = false;
        state.error = action.payload || "Unable to load referral data.";
        state.hasLoaded = true;
      });
  },
});

export const { setReferralCurrentPage } = referralSlice.actions;

export const selectReferralState = (state) => state.referral;

export default referralSlice.reducer;
